/**
 * Investor Drawdown React Query Hooks
 *
 * Hooks for fetching investor-specific drawdown data with batch support
 */

import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { portalQueryKeys } from '@/portal/shared/hooks/queryKeys';
import {
  InvestorDrawdownService,
  InvestorDrawdownSummary,
  InvestorDrawdownDetail,
} from '../services/drawdowns-service';

/**
 * Hook to fetch all drawdowns for the current investor with batch information
 */
export const useInvestorDrawdowns = (params?: {
  status?: 'pending' | 'partial' | 'paid' | 'overdue' | 'all';
  limit?: number;
  skip?: number;
}) => {
  return useApiQuery<InvestorDrawdownSummary[]>(
    portalQueryKeys.transactions.drawdowns(params),
    () => InvestorDrawdownService.getInvestorDrawdowns(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

/**
 * Hook to fetch detailed information about a specific drawdown
 */
export const useInvestorDrawdownDetail = (drawdownId: string | null) => {
  return useApiQuery<InvestorDrawdownDetail>(
    [...portalQueryKeys.transactions.drawdowns(), drawdownId],
    () => InvestorDrawdownService.getInvestorDrawdownDetail(drawdownId!),
    {
      enabled: !!drawdownId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};
